<template>
  <div
    class="solfigr_dash solfidash"
    :class="{
      solfidash_column: dashLayoutColumn,
    }"
  >
    <div
      v-if="shStatus.includes('error')"
      class="solfidash_content anim-fade-in"
    >
      <div
        class="gw_box f-center f-column flex-1 pa-4"
        style="gap: 1rem; position: relative"
      >
        <div
          class="mdi mdi-alert-circle-outline"
          style="
            font-size: 8rem;
            z-index: 4;
            color: var(--clr-error);
            height: 8rem;
            display: flex;
            align-items: center;
            justify-content: center;
          "
        />
        <div class="f-column f-center z-index_4">
          <div
            class="weight_700"
            v-html="$t('messages.oops_error')"
          ></div>
          <div
            class="text-grey"
            v-html="$t('messages.opps_error_sub')"
          ></div>
        </div>
      </div>
    </div>
    <div
      class="solfidash_content f-column"
      style="gap: 0.5rem"
    >
      <div
        v-if="hasSavedSolutions"
        v-show="!shStatus.includes('loading')"
        class="solfidash_tables"
      >
        <CustomSolutionsTable
          :namespace="namespace"
          :cussolId="cussolId"
          class="flex-1"
          @apply:solution="applySolution"
        >
          <template #title_after>
            <div
              v-if="!hasPortalBtn && hasCreateNewSolutionBtn"
              class="gw_btn gw_btn_primary weight_500 text-medium clr-white ml-auto"
              style="--padding: 0 1rem"
              data-tst="create_new_custom_solution"
              @click="openCustomSolutionsDialog"
            >
              <div class="mdi mdi-plus mr-1"></div>
              {{ $t('btn.new_custom_solution') }}
            </div>
          </template>
          <template #new_custom_solution_btn>
            <div
              v-if="hasCreateNewSolutionBtn"
              class="gw_btn-v1 gw_btn_primary mt-4 clr-white"
              data-tst="create_new_custom_solution"
              @click="openCustomSolutionsDialog"
            >
              <div class="mdi mdi-plus mr-2"></div>
              {{ $t('btn.new_custom_solution') }}
            </div>
          </template>
        </CustomSolutionsTable>
      </div>

      <div
        v-if="hasSavedTrees"
        v-show="!shStatus.includes('loading')"
        class="solfidash_tables"
      >
        <SavedTreesTableV1
          :selectedSavedTree="selectedSavedTree"
          :namespace="namespace"
          :hasPresetModes="false"
          class="flex-1"
          @update:loaded="handleLoadedSavedTrees"
          @apply:tree="applyTree"
        >
          <template #title_after>
            <div
              v-if="!hasPortalBtn && hasCreateNewSolutionBtn"
              class="gw_btn gw_btn_primary weight_500 text-medium clr-white"
              style="--padding: 0 1rem"
              data-tst="create_new_custom_solution"
              @click="openCustomSolutionsDialog"
            >
              <div class="mdi mdi-plus mr-1"></div>
              {{ $t('btn.new_custom_solution') }}
            </div>
          </template>
        </SavedTreesTableV1>
      </div>

      <div
        v-if="hasCustomReports"
        v-show="!shStatus.includes('loading')"
        class="solfidash_tables"
      >
        <CustomReportsTable
          :namespace="namespace"
          class="flex-1"
          @apply:report="item => applyReport({ item })"
        >
          <template #title_after>
            <div
              v-if="!hasPortalBtn && hasCreateNewSolutionBtn"
              class="gw_btn gw_btn_primary weight_500 text-medium clr-white ml-auto"
              style="--padding: 0 1rem"
              data-tst="create_new_custom_solution_reports_tab"
              @click="openCustomSolutionsDialog"
            >
              <div class="mdi mdi-plus mr-1"></div>
              {{ $t('btn.new_custom_report') }}
            </div>
          </template>
        </CustomReportsTable>
      </div>

      <portal
        v-if="hasPortalBtn"
        :key="btnPortalName"
        :to="btnPortalName"
      >
        <div class="d-flex w-100 f-row">
          <LinkGuide
            v-if="!$i18n.g1"
            class="truncate my-auto mr-auto pr-2"
            :link="links_url_links[linkGuideKey].url"
            :text="links_url_links[linkGuideKey].text"
            @click.stop
          />

          <div class="role_spacer mx-auto"></div>

          <div
            class="gw_btn gw_btn-v1 weight_500 ml-auto"
            @click="openCustomSolutionsDialog"
          >
            {{
              $t(
                btnPortalName === 'gwiz_custom_ai_reports_custom_solution_btns'
                  ? 'btn.new_custom_report'
                  : 'btn.new_custom_solution',
              )
            }}
          </div>
          <div
            v-if="confirmBtn"
            class="gw_btn gw_btn-v1 gw_btn_primary weight_500 ml-2"
            @click="$emit('click:confirm')"
          >
            {{ $t('btn.confirm') }}
          </div>
        </div>
      </portal>

      <div
        v-if="!hasPortalBtn"
        v-show="!shStatus.includes('loading')"
        class="f-center"
      >
        <LinkGuide
          v-if="!$i18n.g1"
          class="truncate ml-auto"
          :link="links_url_links[linkGuideKey].url"
          :text="links_url_links[linkGuideKey].text"
          @click.stop
        />
      </div>
    </div>
  </div>
</template>

<script>
import LinkGuide from '@/components/AppBar/LinkGuide'
import { SNK } from '@/helpers/helpers'

import SavedTreesTableV1 from '@/components/DataTables/v2/tables/SavedTreesTableV1/SavedTreesTableV1'
import CustomSolutionsTable from '@/components/DataTables/v2/tables/CustomSolutionsTable/CustomSolutionsTable'

import float_mix from '@/mixins/float_mix'
import links_mix from '@/mixins/links_mix'
import CustomReportsTable from '../DataTables/v2/tables/CustomReportsTable/CustomReportsTable.vue'

export default {
  name: 'SolfigrDashboard',
  components: {
    SavedTreesTableV1,
    CustomSolutionsTable,
    LinkGuide,
    CustomReportsTable,
  },
  mixins: [float_mix, links_mix],
  props: {
    namespace: { default: '' },
    confirmBtn: { type: Boolean, default: true },
    tmpNamespace: { default: 'tmp/primary' },
    dimensions: { default: () => ({ height: 0, width: 0 }) },
    instaApply: Boolean,
    channelId: { default: '' },
    hasSavedSolutions: { type: Boolean, default: true },
    hasSavedTrees: Boolean,
    hasCreateNewSolutionBtn: { type: Boolean, default: true },
    hasCustomReports: { type: Boolean, default: false },
    selectedSavedTree: Object,
    hasPortalBtn: Boolean,
    btnPortalName: { default: 'gwiz_custom_custom_solution_btns' }, // gwiz/gwiz_custom _custom_solution_btns
    routerParams: {
      default: () => ({
        push: false,
        replace: false,
        saveLocally: false,
      }),
    },
  },
  emits: ['click:confirm', 'update:savedtree', 'close'],
  data() {
    return {
      shStatus: ['hasitems'],
      // shStatus: ['loading'],
    }
  },
  created() {
    // this endpoint i now used solely for that single initial call (api_poker_custom_solution_history_initial in app.vue)
    // communicate this to BE and best to return this in userSettings like cisited_custom_solutions:true/false
    // so we can display either the mini dashboard or the table
    // or we can ditch the dashboard and just show the table and all this will be much simpler and we could
    // delete api_poker_custom_solution_history alltogether
    const solHistoryData = this.$store.getters.api_get_data('api_poker_custom_solution_history')
    const hasHistory = !!solHistoryData?.results?.[0]
    if (!hasHistory) {
      this.shStatus = ['noitems']
    }
  },
  methods: {
    applySolution(item) {
      if (this.instaApply) {
        if (['StudySolutions'].some(i => i === this.$route.name)) {
          // open in new tab if ctrl or command key is pressed
          const keys = this.$store.getters.hotkeys_pressed
          if (keys?.includes('control') || keys?.includes('command')) {
            const aa = item.solution?.actions || item.actions
            const urlActions = {
              flop_actions: (aa?.flop_actions || []).join('-') || '',
              preflop_actions: (aa?.preflop_actions || []).join('-') || '',
              river_actions: (aa?.river_actions || []).join('-') || '',
              turn_actions: (aa?.turn_actions || []).join('-') || '',
            }
            const l_query = {
              ...this.$route.query,
              ...urlActions,
              cussol_id: item?.solution?.id || item?.id,
              custree_id: item?.solution?.custom_tree?.id || item?.custom_tree?.id,
              history_spot: '',
              solution_type: 'custom',
              soltab: 'strategy',
            }
            this.links_openNewTab({ query: l_query, pathName: 'StudySolutions' })
            return
          }
        }

        this.apply({ item })
        return
      }
      this.setSolQuery({ item })
    },
    applyTree({ item }) {
      if (this.instaApply) {
        this.$store.dispatch('api_poker_custom_user_trees', {
          id: item.id,
          action: SNK('slfrm_handleSavedTreeApply', this.tmpNamespace),
          actionArgs: {
            mode: 'create_tree_then_solution_and_keepboard',
          },
        })
        return
      }
      this.setTreeQuery(item)
    },
    applyReport({ item, routerParams = this.routerParams }) {
      const tableData =
        this.$store.getters.api_get_data('api_custom_aggregated_report_table')?.results || []
      item = item || tableData.find(i => i.id === this.cussTreeId) || tableData[0]

      const solElite = this.$store.getters[SNK('sol_elite', this.tmpNamespace)]
      const currentSolEliteUsingThisTree = solElite.custom_tree_id === item?.custom_tree_id

      const query = {
        ...this.$store.getters[SNK('query_gmfSelector', this.tmpNamespace)],
        ...(item
          ? {
              cussol_id: currentSolEliteUsingThisTree ? solElite.custom_solution_id : '',
              custree_id: item.custom_tree_id,
              preflop_actions: item.preflop_actions,
            }
          : {}),
        solution_type: 'custom',
        gmfs_solution_tab: 'ai_reports',
        soltab: 'reports',
      }
      this.$store.dispatch(SNK('router_update_query', this.tmpNamespace), {
        query,
        ...routerParams,
      })

      if (routerParams.push) {
        this.$store.dispatch(
          'switchers_set_item',
          {
            key: 'sw_tabs_study',
            item: { payload: 'reports' },
          },
          { root: true },
        )
      }
      this.$emit('close')
    },
    setTreeQuery(item) {
      this.$emit('update:savedtree', item)
    },
    setSolQuery({ item, routerParams = this.routerParams }) {
      this.$store.dispatch(SNK('sol_setSolutionQuery', this.tmpNamespace), {
        item,
        routerParams,
      })
    },
    apply({ item, routerParams = this.routerParams }) {
      if (['PracticeTrainer', 'PracticeDrills'].some(i => i === this.$route.name)) {
        this.$store.dispatch(SNK('sol_trainerInitialize', this.namespace))
      }
      if (
        ['StudySolutions', 'PracticeRangeBuilder', 'DetachedGmfSelector'].some(
          i => i === this.$route.name,
        )
      ) {
        const tableData =
          this.$store.getters.api_get_data('api_poker_custom_solutions_table')?.results || []
        item = item || tableData.find(i => i.id === this.cussolId) || tableData[0]

        this.setSolQuery({ item, routerParams })
        if (routerParams.push) {
          this.$store.dispatch(
            'switchers_set_item',
            {
              key: 'sw_tabs_study',
              item: { payload: 'strategy' },
            },
            { root: true },
          )
        }
      }
      this.$emit('close')
    },
    handleLoadedHistory({ items, status }) {
      this.handleLoaded({ items, status })
    },
    handleLoadedSavedTrees({ items, status }) {
      this.handleLoaded({ items, status })
      if (items[0]) {
        this.setTreeQuery(items[0])
      }
    },
    handleLoaded({ items, status }) {
      // console.log({ items, status })
      if (status === 200) {
        this.shStatus = [items.length ? 'hasitems' : 'noitems']
      } else {
        this.shStatus = [status === 403 ? '403' : 'error']
      }
      // this.shStatus = [
      // 'loading',
      // 'hasitems',
      // 'noitems',
      // '403',
      // 'error',
      // ]
    },
    openCustomSolutionsDialog() {
      this.$emit('close')
      const tmpNamespace = 'tmp/extra1'
      const dialog = `solfigr-create-custom-solution-dialog_namespace-${tmpNamespace}_mode-createnew`
      this.$store.dispatch('router_set_dialogs', dialog)
    },
  },
  computed: {
    dashVersion() {
      const v = this.$store.getters.solfigrDashboard
      return (
        {
          v1: 'v1',
          v2: 'v2',
        }[v] || 'v2'
      )
    },
    cussolId() {
      return this.$store.getters[SNK('query_cussol_id', this.tmpNamespace || this.namespace)]
    },
    cussTreeId() {
      return this.$store.getters[SNK('query_custree_id', this.tmpNamespace || this.namespace)]
    },
    linkGuideKey() {
      return this.$route.name === 'PracticeTrainer' ? 'train_against_custom_solutions' : 'ai_guides'
    },
    dashLayoutColumn() {
      return this.dimensions.width > 0 && this.dimensions.width < 850
    },
  },
}
</script>
<style lang="scss" scoped>
.solfidash {
  min-height: 14rem;
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  position: relative;
  z-index: 4;
  &_img {
    width: var(--size);
  }
  &_content {
    flex: 1;
    display: flex;
    min-height: 0;
    min-width: 0;
    max-width: 100%;
  }
  &_tables {
    display: flex;
    flex: 1;
    gap: 0.5rem;
    min-height: 0;
    min-width: 0;
  }
  &_item {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    min-height: 0;
    max-height: 100%;
    border-radius: 4px;
    --item-clr-back: var(--clr-black-0-3);
    background: var(--item-clr-back);
  }
}
</style>

<style lang="scss">
.solfidash_item_history {
  max-height: 100%;
}
.solfidash_column {
  .solfidash_tables {
    flex-direction: column;
    .solfidash_item_history {
      max-height: 50%;
    }
  }
}
@media (max-width: 850px) {
  .solfidash_tables {
    flex-direction: column;
    .solfidash_item_history {
      max-height: 50%;
    }
  }
}
</style>
